import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { clsx } from 'clsx';
import { Loader2 } from 'lucide-react';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';
export type ButtonSize = 'sm' | 'md' | 'lg';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Shows a spinner and blocks the click without changing the button's width. */
  loading?: boolean;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
  fullWidth?: boolean;
  /** Square button for a lone icon. Supply an aria-label when you use it. */
  iconOnly?: boolean;
}

const VARIANTS: Record<ButtonVariant, string> = {
  primary:
    'border border-transparent bg-accent text-accent-ink shadow-card hover:bg-accent-strong active:bg-accent-strong',
  secondary:
    'border border-line-strong bg-surface text-ink hover:border-faint hover:bg-surface-2 active:bg-surface-2',
  ghost: 'bg-transparent text-ink-2 border border-transparent hover:bg-surface-2 hover:text-ink',
  danger: 'bg-danger text-white border border-transparent hover:opacity-90 active:opacity-100',
};

const SIZES: Record<ButtonSize, string> = {
  sm: 'tap-safe h-9 gap-1.5 rounded-control px-3 text-micro',
  md: 'tap-safe h-10 gap-2 rounded-control px-4 text-body',
  lg: 'tap-safe h-11 gap-2 rounded-control px-5 text-body',
};

const ICON_SIZES: Record<ButtonSize, string> = {
  sm: 'tap-safe h-9 w-9 rounded-control',
  md: 'h-9 w-9 rounded-sm',
  lg: 'h-11 w-11 rounded-md',
};

/**
 * The button's classes, for the few links that must look like one — a "tel:"
 * or WhatsApp link stays a real <a> so the phone handles it (19 Oct 2026).
 */
export function buttonClass({
  variant = 'secondary',
  size = 'md',
  iconOnly = false,
  fullWidth = false,
  className,
}: { variant?: ButtonVariant; size?: ButtonSize; iconOnly?: boolean; fullWidth?: boolean; className?: string | false | null } = {}): string {
  return clsx(
    'relative inline-flex shrink-0 items-center justify-center font-medium',
    'transition-[background-color,border-color,color,opacity,transform,box-shadow] duration-150',
    // Pressing seats the button against the board: down one pixel, contact
    // shadow gone. Transform and shadow only — nothing that reflows.
    'motion-safe:active:translate-y-px active:shadow-none',
    // On touch devices every button's hit area grows to the 44px floor,
    // whatever its visual size — the slop is invisible and desktop unchanged.
    "pointer-coarse:before:absolute pointer-coarse:before:-inset-1.5 pointer-coarse:before:content-['']",
    'disabled:cursor-not-allowed disabled:opacity-55',
    'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent',
    iconOnly ? ICON_SIZES[size] : SIZES[size],
    VARIANTS[variant],
    fullWidth && 'w-full',
    className,
  );
}

/**
 * The one button. Four variants, and no fifth — an operations console with five
 * button styles has none.
 *
 * `loading` keeps the label in the flow at zero opacity so the button does not
 * resize under the cursor mid-click, which is how double submissions happen.
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'secondary',
    size = 'md',
    loading = false,
    leftIcon,
    rightIcon,
    fullWidth = false,
    iconOnly = false,
    className,
    disabled,
    children,
    type = 'button',
    ...rest
  },
  ref,
) {
  const isDisabled = disabled || loading;

  return (
    <button
      ref={ref}
      type={type}
      disabled={isDisabled}
      aria-busy={loading || undefined}
      className={buttonClass({ variant, size, iconOnly, fullWidth, className })}
      {...rest}
    >
      {loading && (
        <Loader2
          aria-hidden
          className="absolute h-4 w-4 animate-spin motion-reduce:animate-none"
        />
      )}
      <span
        className={clsx(
          'inline-flex items-center',
          iconOnly ? '' : size === 'sm' ? 'gap-1.5' : 'gap-2',
          loading && 'opacity-0',
        )}
      >
        {leftIcon}
        {children}
        {rightIcon}
      </span>
    </button>
  );
});
